export class ManagedBufferSource {
	private _audioContext: AudioContext;
	private _audioStreamDestination: MediaStreamAudioDestinationNode;
	private _audioBuffer: AudioBuffer;

	public sourceNode: AudioBufferSourceNode | null = null;
	private _gainNode: GainNode;
	private _startTime: number = 0;
	private _offset: number = 0;
	private _playing: boolean = false;
	private _muted: boolean = false;

	constructor(audioContext: AudioContext, audioStreamDestination: MediaStreamAudioDestinationNode, audioBuffer: AudioBuffer) {
		this._audioContext = audioContext;
		this._audioStreamDestination = audioStreamDestination;
		this._audioBuffer = audioBuffer;

		// Create and connect the gain node
		this._gainNode = this._audioContext.createGain();
		this._gainNode.connect(this._audioStreamDestination);
		this._gainNode.connect(this._audioContext.destination);

		const channelData = this._audioBuffer.getChannelData(0);

		let silent = true;
		for (let i = 0; i < channelData.length; i++) {
			if (Math.abs(channelData[i]) > 0.0001) {
				silent = false;
				break;
			}
		}

		if (this._audioBuffer.length <= 0 || this._audioBuffer.duration <= 0 || silent) {
			console.warn('AudioBuffer appears to be empty or silent.');
		}

		if (this._audioContext.state === 'suspended') {
			console.warn('Could not play audio: Audio context suspended.');
		}
	}

	play(time: number = 0): void {
		if (this._playing) {
			this.stop();
		}
		this.sourceNode = this._createSource();
		this._offset = time;
		this._startTime = this._audioContext.currentTime;
		this.sourceNode.start(0, time);
		this._playing = true;
	}

	stop(): void {
		if (this._playing && this.sourceNode) {
			this.sourceNode.stop();
			this._offset = this._getCurrentTime();
			this._playing = false;
		}
	}

	resume(): void {
		if (!this._playing) {
			this.play(this._offset);
		}
	}

	/**
	 * Release the current source node. Called on every natural end via `onended`, so this deliberately
	 * leaves the gain node connected and the instance replayable — `play()` builds a fresh source node.
	 * For permanent teardown use `destroy()`.
	 */
	dispose(): void {
		if (!this.sourceNode) return;
		this.sourceNode.disconnect();
		this.sourceNode = null;
	}

	/**
	 * Permanently tear the instance down: stop playback and release the gain node.
	 *
	 * `dispose()` is not enough on its own — it never stops a playing node and never disconnects
	 * `_gainNode`, which is wired to both the stream destination and `audioContext.destination`, so an
	 * instance dropped without this leaks a GainNode and keeps decoding to the end of its buffer. The
	 * instance is unusable afterwards; only call it when the source is genuinely being discarded.
	 */
	destroy(): void {
		if (this.sourceNode) {
			// Stop re-entering dispose() from the stop() below — we release the node here instead.
			this.sourceNode.onended = null;
			try {
				if (this._playing) this.sourceNode.stop();
			} catch (error) {
				console.warn('Error stopping audio source: ', error);
			}
			this.sourceNode.disconnect();
			this.sourceNode = null;
		}
		this._playing = false;
		this._gainNode.disconnect();
	}

	private _getCurrentTime(): number {
		if (this._playing) {
			return this._offset + (this._audioContext.currentTime - this._startTime);
		} else {
			return this._offset;
		}
	}

	private _createSource(): AudioBufferSourceNode {
		const source = this._audioContext.createBufferSource();
		source.buffer = this._audioBuffer;

		// Connect source through gain node
		source.connect(this._gainNode);

		source.onended = () => {
			this._playing = false;
			this.dispose();
		};

		return source;
	}

	get currentTime(): number {
		return this._getCurrentTime();
	}

	get paused(): boolean {
		return !this._playing;
	}

	get muted(): boolean {
		return this._muted;
	}

	set muted(value: boolean) {
		this._muted = value;
		this._gainNode.gain.setValueAtTime(value ? 0 : 1, this._audioContext.currentTime);
	}
}
